/**
 * AssetDetailModal — single-asset edit modal w/ classifier panel + audit log.
 *
 * Extracted from BrandAssetGallery.jsx during May 2026 refactor.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Tag, Camera, ExternalLink, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { API, ORIGINS, POOLS, authedFetch } from "./utils";
import { ClassifierPanel } from "./ClassifierPanel";

function AssetDetailModal({ asset, workspaceId, onClose, onUpdated }) {
  const [editing, setEditing] = useState({
    origin: asset?.origin || "unknown",
    pool: asset?.pool || "subjects",
    is_active: !!asset?.is_active,
  });
  const [reason, setReason] = useState("");
  const [auditLog, setAuditLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);

  useEffect(() => {
    if (!asset) return;
    setEditing({
      origin: asset.origin || "unknown",
      pool: asset.pool || "subjects",
      is_active: !!asset.is_active,
    });
    setReason("");
    setLoadingAudit(true);
    authedFetch(`${API}/v2/workspaces/${workspaceId}/brand/assets/${asset.asset_id}/audit-log?limit=20`)
      .then((r) => r.json())
      .then((d) => setAuditLog(d.entries || []))
      .catch(() => setAuditLog([]))
      .finally(() => setLoadingAudit(false));
  }, [asset?.asset_id]);

  if (!asset) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/assets/${asset.asset_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            origin: editing.origin,
            pool: editing.pool,
            is_active: editing.is_active,
            reason: reason || null,
          }),
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      const changed = data.changed_fields || [];
      if (changed.length === 0) {
        toast.message("No changes");
      } else {
        toast.success(`Saved: ${changed.join(", ")}`);
        onUpdated(data.asset);
      }
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="gallery-detail-modal">
        <DialogHeader>
          <DialogTitle className="truncate" data-testid="gallery-detail-title">
            {asset.file_name || asset.asset_id}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          {/* Image */}
          <div className="bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]">
            <img
              src={asset.full_url}
              alt={asset.file_name}
              className="max-w-full max-h-[60vh] object-contain"
              data-testid="gallery-detail-image"
            />
          </div>

          {/* Edit panel */}
          <div className="space-y-4">
            <div className="space-y-1 text-xs text-slate-600">
              <div>
                <span className="font-semibold">Asset ID:</span> {asset.asset_id}
              </div>
              <div>
                <span className="font-semibold">Source folder:</span>{" "}
                <span className="break-all">{asset.source_folder || "(unknown)"}</span>
              </div>
              <div>
                <span className="font-semibold">MIME:</span> {asset.mime_type}
                {" · "}
                <span className="font-semibold">Size:</span>{" "}
                {Math.round((asset.size_bytes || 0) / 1024)} KB
              </div>
              <div>
                <span className="font-semibold">Ingested:</span>{" "}
                {asset.created_at ? new Date(asset.created_at).toLocaleString() : "—"}
              </div>
            </div>

            {/* Phase 1.3 — Attribution panel for stock assets. Mandatory
                surface per Unsplash API guidelines; Pexels appreciates it
                even when not required. Phase 3 Art Director reads the same
                attribution_text from asset_metadata to embed in post
                captions. */}
            {asset.origin === "stock" && asset.asset_metadata?.attribution_text ? (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5"
                data-testid="asset-detail-attribution"
              >
                <div className="flex items-center gap-2 font-semibold text-slate-700">
                  <Camera className="h-3.5 w-3.5" />
                  Attribution
                  {asset.asset_metadata?.attribution_required ? (
                    <Badge className="bg-amber-100 text-amber-900 border-0 text-[10px]">
                      Required on publish
                    </Badge>
                  ) : null}
                </div>
                <div className="italic text-slate-700" data-testid="asset-detail-attribution-text">
                  {asset.asset_metadata.attribution_text}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {asset.asset_metadata?.photographer?.url ? (
                    <a
                      href={asset.asset_metadata.photographer.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      data-testid="asset-detail-photographer-link"
                    >
                      <ExternalLink className="h-3 w-3" /> Photographer
                    </a>
                  ) : null}
                  {asset.asset_metadata?.source_url ? (
                    <a
                      href={asset.asset_metadata.source_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      data-testid="asset-detail-source-link"
                    >
                      <ExternalLink className="h-3 w-3" /> View on {
                        asset.asset_metadata.source === "unsplash" ? "Unsplash" : "Pexels"
                      }
                    </a>
                  ) : null}
                </div>
                {asset.asset_metadata?.search_query ? (
                  <div className="text-slate-500">
                    Ingested via search: <span className="italic">"{asset.asset_metadata.search_query}"</span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Phase 1.4 — Classifier verdict panel */}
            <ClassifierPanel
              asset={asset}
              auto={asset.auto_classification}
              workspaceId={workspaceId}
              onAssetUpdated={onUpdated}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium block">Active</label>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editing.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: !!v })}
                  data-testid="gallery-detail-active"
                />
                <span className="text-sm">Visible to renderer / picker</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium block">Origin</label>
              <Select
                value={editing.origin}
                onValueChange={(v) => setEditing({ ...editing, origin: v })}
              >
                <SelectTrigger data-testid="gallery-detail-origin">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORIGINS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium block">Pool</label>
              <Select
                value={editing.pool}
                onValueChange={(v) => setEditing({ ...editing, pool: v })}
              >
                <SelectTrigger data-testid="gallery-detail-pool">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POOLS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium block">Reason (audit log)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you changing this?"
                data-testid="gallery-detail-reason"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <Tag className="h-4 w-4" /> Audit trail
              </div>
              <div
                className="max-h-40 overflow-y-auto border rounded p-2 bg-slate-50 text-xs space-y-1.5"
                data-testid="gallery-detail-audit"
              >
                {loadingAudit ? (
                  <div className="text-slate-500">Loading…</div>
                ) : auditLog.length === 0 ? (
                  <div className="text-slate-500">No audit entries.</div>
                ) : (
                  auditLog.map((e) => (
                    <div key={e.audit_id} className="border-b border-slate-200 pb-1 last:border-b-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{e.action}</span>
                        <span className="text-slate-500">
                          {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                        </span>
                      </div>
                      <div className="text-slate-600">
                        {e.actor_email || "system"}
                        {e.reason ? ` — ${e.reason}` : ""}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="gallery-detail-cancel">
            Close
          </Button>
          <Button onClick={save} disabled={saving} data-testid="gallery-detail-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Phase 1.4 Triage Banner ──────────────────────────────────────────────
// Sits above the gallery grid. Pulls /brand/triage-estimate periodically and
// surfaces:
//   • verdict distribution (safe / ambiguous / unsafe / unclassified)
//   • estimated time to clear the ambiguous queue

export default AssetDetailModal;
export { AssetDetailModal };
